import { useState } from 'react'
import { OrderCodeLink } from './order-code-link'
import type { ReactNode } from 'react'

import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {
    useChangePaymentReceiptMethod,
    useCustomerPaymentReceipt,
} from '@/hooks/use-customer-debt-query'
import { useAuth } from '@/hooks/use-auth'
import { Button } from '@/components/ui/button'
import {
    ChangePaymentMethodDialog,
    MethodChangeLines,
    isSwitchablePayment,
} from '@/components/orders/change-payment-method-dialog'
import { formatDocCode, formatVN, formatVnd } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

function methodLabel(method: string): string {
    switch (method) {
        case 'cash':
            return m.payment_method_cash()
        case 'bank_transfer':
            return m.payment_method_bank()
        case 'card':
            return m.customer_debt_payment_method_card()
        case 'credit':
            return m.order_credit_apply_label()
        default:
            return method
    }
}

function Field({ label, value }: { label: string; value: ReactNode }) {
    return (
        <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">{label}</span>
            <span className="text-right font-medium">{value}</span>
        </div>
    )
}

export function CustomerPaymentReceiptDialog({
    customerId,
    receiptId,
    open,
    onOpenChange,
}: {
    customerId: string
    receiptId?: string
    open: boolean
    onOpenChange: (open: boolean) => void
}) {
    const { data, isLoading } = useCustomerPaymentReceipt(customerId, receiptId)
    const { hasPermission } = useAuth()
    const change = useChangePaymentReceiptMethod(customerId)
    // Đổi hình thức cả phiếu (M-152): phiếu lập nhầm Tiền mặt / Chuyển khoản.
    const [changing, setChanging] = useState(false)
    const canChange =
        hasPermission('customers.payment') &&
        !!data &&
        isSwitchablePayment(data.receipt.payment_method)

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>{m.receipt_detail_title()}</DialogTitle>
                </DialogHeader>

                {isLoading || !data ? (
                    <div className="py-6 text-center text-sm text-muted-foreground">
                        {m.common_processing()}
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div className="space-y-1.5 rounded-md border p-3 text-sm">
                            <Field
                                label={m.receipt_detail_id()}
                                value={
                                    <span className="font-mono text-xs">
                                        {formatDocCode(data.receipt.id)}
                                    </span>
                                }
                            />
                            <Field
                                label={m.customer_debt_payment_amount()}
                                value={formatVnd(data.receipt.amount)}
                            />
                            <Field
                                label={m.customer_debt_payment_method()}
                                value={
                                    <span className="inline-flex items-center gap-2">
                                        {methodLabel(
                                            data.receipt.payment_method,
                                        )}
                                        {canChange && (
                                            <Button
                                                type="button"
                                                variant="link"
                                                size="sm"
                                                className="h-auto px-0 md:h-auto md:px-0"
                                                onClick={() =>
                                                    setChanging(true)
                                                }
                                            >
                                                {m.order_payment_method_change_button()}
                                            </Button>
                                        )}
                                    </span>
                                }
                            />
                            <MethodChangeLines
                                changes={data.receipt.method_changes}
                            />
                            <Field
                                label={m.customer_debt_payment_date()}
                                value={formatVN(data.receipt.occurred_at)}
                            />
                            {data.receipt.collector_name && (
                                <Field
                                    label={m.receipt_detail_collector()}
                                    value={data.receipt.collector_name}
                                />
                            )}
                            {data.receipt.note && (
                                <Field
                                    label={m.customer_debt_payment_note()}
                                    value={data.receipt.note}
                                />
                            )}
                        </div>

                        <div>
                            <div className="mb-1.5 text-sm font-medium">
                                {m.receipt_detail_allocations()}
                            </div>
                            {data.allocations.length === 0 ? (
                                <p className="text-sm text-muted-foreground">
                                    {m.receipt_detail_no_allocations()}
                                </p>
                            ) : (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>
                                                {m.customer_debt_payment_order_col()}
                                            </TableHead>
                                            <TableHead className="text-right">
                                                {m.receipt_detail_alloc_amount_col()}
                                            </TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {data.allocations.map((a, i) => (
                                            <TableRow key={i}>
                                                <TableCell className="font-mono text-xs">
                                                    <OrderCodeLink
                                                        orderId={
                                                            a.sales_order_id
                                                        }
                                                        code={a.order_code}
                                                    />
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    {formatVnd(a.amount)}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </div>
                    </div>
                )}
            </DialogContent>
            <ChangePaymentMethodDialog
                subject={changing && data ? data.receipt : null}
                note={m.order_payment_method_change_receipt_note()}
                pending={change.isPending}
                onOpenChange={(next) => {
                    if (!next && !change.isPending) setChanging(false)
                }}
                onConfirm={(method) => {
                    if (!receiptId) return
                    change.mutate(
                        { receiptId, method },
                        { onSettled: () => setChanging(false) },
                    )
                }}
            />
        </Dialog>
    )
}
